import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import ProfilePreviewDialog from "crqlar-react-common/dist/components/profilePreview/ProfilePreviewDialog";
import {prefetchProfilePastVisits} from "crqlar-react-common/dist/components/profilePreview/pastVisits/profilePastVisits.query";
import SurveyInviteButton from "../surveys/share/SurveyInviteButton";
import {DataFilterOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import type {ProfileListRequestDTO} from "crqlar-react-common/dist/models/profile/profileListRequest.dto";
import type {ProfileListItem} from "crqlar-react-common/dist/models/profile/profileListItem.model";
import {ProfileAggregationService} from "crqlar-react-common/dist/services/aggregation/profileAggregation.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {useProfileFilterOptions} from "crqlar-react-common/dist/utils/hooks/profile/useProfiles";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useSnackbar} from "notistack";
import {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery, useQueryClient} from "react-query";
import {useSearchParams} from "react-router-dom";
import {
    RightSidebarCreateButton,
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailLayout,
} from "../layout/rightSidebar/RightSidebarRail";
import {
    StayStatusFilterValue,
    StayStatusRailFilter,
} from "../layout/rightSidebar/StayStatusRailFilter";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailExport,
    ServerTableRailFilter,
    ServerTableRailFullscreen,
    ServerTableRailPrint,
    ServerTableRailSearch,
    ServerTableRailSort,
    serverTableExportLimit,
    type ServerTableExportQuery,
    useServerTable,
} from "../common/serverTable";
import ProfileCreateDialog from "../system/profile/ProfileCreateDialog";
import {useProfileListColumns} from "./profileListColumns";

export default function CrmGuestList() {
    useTrackScreenVisit("profile list");

    const {t} = useTranslation();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();
    const readOnlyUser = useReadOnly();
    const filterOptionsQuery = useProfileFilterOptions();
    const [stayPeriod, setStayPeriod] = useState<StayStatusFilterValue>("ALL");
    const [selectedProfileId, setSelectedProfileId] = useState<string>();
    const [createProfileOpen, setCreateProfileOpen] = useState(false);
    const requestedProfileId = searchParams.get("profileId") ?? undefined;

    useEffect(() => {
        if (requestedProfileId) setSelectedProfileId(requestedProfileId);
    }, [requestedProfileId]);

    // The preview lists the guest's bookings: load them alongside the profile, not after it.
    useEffect(() => {
        if (selectedProfileId) prefetchProfilePastVisits(queryClient, selectedProfileId);
    }, [selectedProfileId, queryClient]);

    // List rows are lightweight ProfileListItems; load the full Profile only when opening the preview.
    const selectedProfileQuery = useQuery(
        ["crm-guest-list-profile", selectedProfileId],
        () => ProfileService.getProfileById(selectedProfileId!, true),
        {
            enabled: Boolean(selectedProfileId),
            onError: () => {
                enqueueSnackbar(t("Could not load profile"), {variant: "error"});
                setSelectedProfileId(undefined);
            },
        },
    );

    const columns = useProfileListColumns();

    const controller = useServerTable<ProfileListItem>({
        tableId: "crm-guest-list",
        columns,
        getRowId: profile => profile.id,
        onRowClick: profile => setSelectedProfileId(profile.id),
        initialSorting: [{id: "id", desc: true}],
        queryDependencies: [stayPeriod],
        queryFn: async ({pagination, searchQuery, filterRules, sorting}) => {
            const today = new Date();
            const selectedDateRange = {
                start: startOfDay(today),
                end: endOfDay(today),
            };
            const page = await ProfileAggregationService.listProfiles({
                query: searchQuery || undefined,
                followMerge: true,
                overlaps: stayPeriod === "STAYING" ? selectedDateRange : undefined,
                expectedStart: stayPeriod === "CHECKING_IN" ? selectedDateRange : undefined,
                expectedEnd: stayPeriod === "CHECKING_OUT" ? selectedDateRange : undefined,
                advancedFilter: filterRules.length
                    ? {
                        filterGroups: [{
                            filterRules,
                            operator: DataFilterOperator.AND,
                        }],
                    }
                    : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0,
            };
        },
    });

    // The export's request: the list's own, without paging.
    const exportRequest = useCallback((
        {searchQuery, filterRules, sorting}: ServerTableExportQuery,
    ): ProfileListRequestDTO => {
        const today = new Date();
        const selectedDateRange = {
            start: startOfDay(today),
            end: endOfDay(today),
        };

        return {
            query: searchQuery || undefined,
            followMerge: true,
            overlaps: stayPeriod === "STAYING" ? selectedDateRange : undefined,
            expectedStart: stayPeriod === "CHECKING_IN" ? selectedDateRange : undefined,
            expectedEnd: stayPeriod === "CHECKING_OUT" ? selectedDateRange : undefined,
            advancedFilter: filterRules.length
                ? {
                    filterGroups: [{
                        filterRules,
                        operator: DataFilterOperator.AND,
                    }],
                }
                : undefined,
            limit: serverTableExportLimit(sorting),
        };
    }, [stayPeriod]);

    const handleProfileUpdate = (profile: Profile) => {
        queryClient.setQueryData(
            ["crm-guest-list-profile", selectedProfileId],
            profile,
        );
        void controller.query.refetch();
    };

    return (
        <>
            <Box sx={{display: "flex", flex: 1, minWidth: 0, minHeight: 0}}>
                <ServerTableProvider controller={controller}>
                    <ServerTableFrame>
                        <RightSidebarRailLayout>
                            <ServerTable/>

                            {selectedProfileQuery.isLoading && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        zIndex: 2,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        bgcolor: "rgba(255, 255, 255, 0.5)",
                                        pointerEvents: "none",
                                    }}
                                >
                                    <CircularProgress size={28}/>
                                </Box>
                            )}

                            <RightSidebarRail>
                                <ServerTableRailSearch/>
                                <ServerTableRailSort/>
                                <StayStatusRailFilter
                                    value={stayPeriod}
                                    onChange={setStayPeriod}
                                />
                                <ServerTableRailFilter
                                    options={filterOptionsQuery.data ?? []}
                                    disabled={filterOptionsQuery.isLoading || filterOptionsQuery.isError}
                                />
                                <ServerTableRailFullscreen/>
                                <ServerTableRailColumns/>
                                <RightSidebarDivider/>
                                <RightSidebarCreateButton
                                    label={t("Create profile")}
                                    disabled={readOnlyUser}
                                    onClick={() => setCreateProfileOpen(true)}
                                />
                                <RightSidebarDivider/>
                                <ServerTableRailPrint title={t("Guest List")}/>
                                <ServerTableRailExport
                                    list="crm-guests"
                                    title={t("Guest List")}
                                    buildRequest={exportRequest}
                                />
                            </RightSidebarRail>
                        </RightSidebarRailLayout>
                    </ServerTableFrame>
                </ServerTableProvider>
            </Box>

            <ProfilePreviewDialog
                open={Boolean(selectedProfileQuery.data)}
                onClose={() => setSelectedProfileId(undefined)}
                profile={selectedProfileQuery.data}
                setProfile={handleProfileUpdate}
                extraActions={<SurveyInviteButton profileId={selectedProfileQuery.data?.id}/>}
            />

            {createProfileOpen && (
                <ProfileCreateDialog
                    open
                    onClose={() => setCreateProfileOpen(false)}
                    onCreated={() => {
                        setCreateProfileOpen(false);
                        void controller.query.refetch();
                    }}
                />
            )}
        </>
    );
}
